import { WithTranslateProps } from '../common/WithTranslate';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Button, Intent, Switch } from '@blueprintjs/core';
import { TLCode } from '../../common/enums/TLCode';
import { TranslatedMessage } from '../common/translation';
import { TextInput } from '../common/input/basicInputs';
import { Municipality } from '../../store/models/Municipality';

interface NewAddressFormProps extends WithTranslateProps {
  onSave?: (newLat: string, newLong: string) => void;
  onReset?: () => void;
  calculateCoordinates: (address: string) => Promise<[number, number, typeof Municipality]>;
  convertLambert2008ToWGS84?: (x: number, y: number) => Promise<[number, number]>;
  convertWGS84ToLambert2008?: (lat: number, long: number) => [number, number];
  feededCoordinatesLat?: string;
  feededCoordinatesLong?: string;
  feededAddress?: string;
  pharmacyLabelColor?: string;
  pharmacyTextColor?: string;
  resetTrigger?: string | number;
}

export const NewAddressForm = ({
  translate,
  onSave,
  onReset,
  calculateCoordinates,
  convertLambert2008ToWGS84,
  convertWGS84ToLambert2008,
  feededCoordinatesLat,
  feededCoordinatesLong,
  feededAddress,
  pharmacyLabelColor,
  pharmacyTextColor,
  resetTrigger,
}: NewAddressFormProps): JSX.Element => {
  const [buttonCalculateNewCoordinatesDisabled, setButtonCalculateNewCoordinatesDisabled] = useState(true);
  const [newAddress, setNewAddress] = useState(undefined as string | undefined);
  const [newLatLambert2008, setNewLatLambert2008] = useState(undefined as string | undefined);
  const [newLongLambert2008, setNewLongLambert2008] = useState(undefined as string | undefined);
  const [newLat, setNewLat] = useState('');
  const [newLong, setNewLong] = useState('');
  const [searchByLambert2008, setSearchByLambert2008] = useState(false);
  const [displayLambert2008, setDisplayLambert2008] = useState(false);

  useEffect(() => {
    if (resetTrigger) {
      setNewAddress(undefined);
      setNewLatLambert2008(undefined);
      setNewLongLambert2008(undefined);
      setNewLat('');
      setNewLong('');
      setButtonCalculateNewCoordinatesDisabled(true);
      setSearchByLambert2008(false);
      setDisplayLambert2008(false);
    }
  }, [resetTrigger]);

  const calculateCoordinatesAndSet = useCallback(async () => {
    let lat, long: number;
    if (searchByLambert2008 && convertLambert2008ToWGS84) {
      const result = await convertLambert2008ToWGS84(Number(newLatLambert2008), Number(newLongLambert2008));
      lat = result[0];
      long = result[1];
    } else {
      const result = await calculateCoordinates(newAddress + '');
      lat = result[0];
      long = result[1];
    }
    setNewLat(lat.toString());
    setNewLong(long.toString());
  }, [newAddress, searchByLambert2008, convertLambert2008ToWGS84, newLatLambert2008, newLongLambert2008]);

  const resetAddressAndCoordinates = useCallback(() => {
    setNewAddress(undefined);
    setNewLatLambert2008(undefined);
    setNewLongLambert2008(undefined);
    setNewLat('');
    setNewLong('');
    if (onReset) onReset();
  }, []);

  const coordinatesLambert2008PharmacySelected = useMemo(() => {
    if (
      newLat === undefined ||
      newLong === undefined ||
      Number.isNaN(newLat) ||
      Number.isNaN(newLong) ||
      newLat === '' ||
      newLong === '' ||
      convertWGS84ToLambert2008 === undefined
    )
      return undefined;

    return convertWGS84ToLambert2008(Number(newLat), Number(newLong));
  }, [convertWGS84ToLambert2008, newLat, newLong]);

  useEffect(() => {
    if (
      feededCoordinatesLat !== undefined &&
      feededCoordinatesLong !== undefined &&
      (feededCoordinatesLat != newLat || feededCoordinatesLong != newLong)
    ) {
      setNewLat(feededCoordinatesLat);
      setNewLong(feededCoordinatesLong);
    }
  }, []);

  useEffect(() => {
    if (feededAddress !== undefined && feededAddress != newAddress) {
      setNewAddress(feededAddress);
    }
  }, [feededAddress]);

  return (
    <>
      <DivLine>
        <DivColumn>
          <DivLine>
            {convertLambert2008ToWGS84 && (
              <>
                <DivColumn>
                  <Switch
                    checked={searchByLambert2008}
                    onChange={() => setSearchByLambert2008(!searchByLambert2008)}
                    innerLabel={translate(TLCode.TRANSFER_Address)}
                    innerLabelChecked={translate(TLCode.TRANSFER_Coordinates)}
                  />
                </DivColumn>
              </>
            )}

            <DivColumn
              style={{
                width: '150px',
                color: pharmacyTextColor !== undefined ? pharmacyTextColor : 'black',
                background: pharmacyLabelColor !== undefined ? pharmacyLabelColor : 'white',
                marginRight: '5px',
                paddingLeft: '3px',
              }}
            >
              <TranslatedMessage
                id={searchByLambert2008 ? TLCode.TRANSFER_NewLambert2008Coordinates : TLCode.TRANSFER_NewAddress}
              />
            </DivColumn>
            {searchByLambert2008 ? (
              <>
                <DivColumn style={{ width: '30px', textAlign: 'right', paddingRight: '5px' }}>
                  <TranslatedMessage id={TLCode.PHARMACY_X_Lambert2008} />
                </DivColumn>
                <DivColumn style={{ width: '80px' }}>
                  <TextInput
                    name="newLatitudeLambert2008"
                    required={true}
                    fill={true}
                    onChange={(e) => {
                      setNewLatLambert2008(e.target.value);
                      setButtonCalculateNewCoordinatesDisabled(
                        newLatLambert2008 === undefined ||
                        isNaN(Number(newLatLambert2008)) ||
                        newLongLambert2008 === undefined ||
                        isNaN(Number(newLongLambert2008)),
                      );
                    }}
                  />
                </DivColumn>
                <DivColumn style={{ width: '30px', textAlign: 'right', paddingRight: '5px' }}>
                  <TranslatedMessage id={TLCode.PHARMACY_Y_Lambert2008} />
                </DivColumn>
                <DivColumn style={{ width: '80px' }}>
                  <TextInput
                    name="newLongitudeLambert2008"
                    required={true}
                    fill={true}
                    onChange={(e) => {
                      setNewLongLambert2008(e.target.value);
                      setButtonCalculateNewCoordinatesDisabled(
                        newLatLambert2008 === undefined ||
                        isNaN(Number(newLatLambert2008)) ||
                        newLongLambert2008 === undefined ||
                        isNaN(Number(newLongLambert2008)),
                      );
                    }}
                  />
                </DivColumn>
              </>
            ) : (
              <DivColumn style={{ width: '300px', textAlign: 'right', paddingRight: '5px' }}>
                <TextInput
                  name="newAddress"
                  required={true}
                  fill={false}
                  value={newAddress || ''}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    if (newValue !== newAddress) {
                      resetAddressAndCoordinates();
                      setNewAddress(newValue);
                      setButtonCalculateNewCoordinatesDisabled(newValue === undefined || newValue === '');
                    }
                  }}
                />
              </DivColumn>
            )}
            <DivColumn style={{ marginLeft: '5px' }}>
              <Button
                text={translate(TLCode.TRANSFER_CalculateNewCoordinates)}
                intent={Intent.SUCCESS}
                onClick={() => calculateCoordinatesAndSet()}
                disabled={buttonCalculateNewCoordinatesDisabled}
              />
            </DivColumn>
          </DivLine>
        </DivColumn>
        <DivColumn>
          <DivLine style={{ marginLeft: '40px' }}>
            {displayLambert2008 ? (
              <>
                <DivLine style={{ width: '220px' }}>
                  <span style={{ width: '40%' }}>
                    <TranslatedMessage id={TLCode.PHARMACY_X_Lambert2008} />
                  </span>
                  &nbsp;&nbsp;
                  <TextInput
                    name="latitudeLambert2008"
                    required={true}
                    fill={false}
                    value={
                      coordinatesLambert2008PharmacySelected !== undefined
                        ? Math.round(coordinatesLambert2008PharmacySelected[0]).toString()
                        : ''
                    }
                    disabled
                    style={{ width: '60%' }}
                  />
                </DivLine>
                <DivLine style={{ width: '220px', marginLeft: '20px' }}>
                  <span style={{ width: '40%' }}>
                    <TranslatedMessage id={TLCode.PHARMACY_Y_Lambert2008} />
                  </span>
                  &nbsp;&nbsp;
                  <TextInput
                    name="longitudeLambert2008"
                    required={true}
                    fill={false}
                    value={
                      coordinatesLambert2008PharmacySelected !== undefined
                        ? Math.round(coordinatesLambert2008PharmacySelected[1]).toString()
                        : ''
                    }
                    disabled
                    style={{ width: '60%' }}
                  />
                </DivLine>
              </>
            ) : (
              <>
                <DivLine style={{ width: '220px' }}>
                  <span style={{ width: '40%', marginRight: '2px' }}>
                    <TranslatedMessage id={TLCode.PHARMACY_Latitude} />
                  </span>
                  &nbsp;&nbsp;
                  <TextInput
                    name="newLatitude"
                    required={true}
                    fill={true}
                    value={newLat}
                    onChange={(e) => setNewLat(e.target.value)}
                    style={{ width: '60%' }}
                  />
                </DivLine>
                <DivLine style={{ width: '220px', marginLeft: '20px', marginRight: '20px' }}>
                  <span style={{ width: '40%' }}>
                    <TranslatedMessage id={TLCode.PHARMACY_Longitude} />
                  </span>
                  &nbsp;&nbsp;
                  <TextInput
                    name="newLongitude"
                    required={true}
                    fill={true}
                    value={newLong}
                    onChange={(e) => setNewLong(e.target.value)}
                    style={{ width: '60%' }}
                  />
                </DivLine>
              </>
            )}
            <DivColumn>
              <Switch
                checked={displayLambert2008}
                onChange={() => setDisplayLambert2008(!displayLambert2008)}
                innerLabel={translate(TLCode.TRANSFER_LatLong)}
                innerLabelChecked={translate(TLCode.TRANSFER_Lambert)}
              />
            </DivColumn>{' '}
            <DivColumn style={{ justifyContent: 'flex-end', marginLeft: '5px' }}>
              {
                //TODO to be disabled when data is not completed
              }
              {onSave && (
                <Button
                  text={translate(TLCode.TRANSFER_Calculate)}
                  intent={Intent.SUCCESS}
                  onClick={() => {
                    onSave(newLat, newLong);
                  }}
                />
              )}
            </DivColumn>
          </DivLine>
        </DivColumn>
      </DivLine>
    </>
  );
};

const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const DivColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;
